/**
 * Domain enums — the single definition shared by the database, the API and the UI.
 *
 * Mongoose schemas, Zod schemas and React components all import from here, so a state
 * cannot be spelled two ways in two places.
 */

// ── Roles and rank ──────────────────────────────────────────────────────────────

export const ROLES = ['SUPER_ADMIN', 'ADMIN', 'CEO', 'HR', 'EMPLOYEE'] as const;
export type Role = (typeof ROLES)[number];

/**
 * Rank grants nothing on its own. It exists solely for the rank-comparison rule:
 * an actor may never create, modify, deactivate or assign a role to a user of equal
 * or higher rank. See docs/AUTHORIZATION_MATRIX.md §5.
 */
export const ROLE_RANK: Record<Role, number> = {
  EMPLOYEE: 0,
  HR: 1,
  CEO: 2,
  ADMIN: 3,
  SUPER_ADMIN: 4,
};

/** System portal operators — not shown on org charts or in manager team lists. */
export function isPlatformOperatorRole(role: Role | null | undefined): boolean {
  return role === 'ADMIN' || role === 'SUPER_ADMIN';
}

// ── Identity ────────────────────────────────────────────────────────────────────

export const USER_STATUSES = ['active', 'pending', 'inactive'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const EMPLOYEE_STATUSES = ['active', 'on_leave', 'inactive'] as const;
export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];

export const EMPLOYMENT_TYPES = [
  'full_time',
  'part_time',
  'contract',
  'intern',
  'consultant',
] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

export const PROBATION_STATUSES = ['not_applicable', 'in_probation', 'confirmed'] as const;
export type ProbationStatus = (typeof PROBATION_STATUSES)[number];

export const DEPARTMENT_STATUSES = ['active', 'archived'] as const;
export type DepartmentStatus = (typeof DEPARTMENT_STATUSES)[number];

// ── Attendance ──────────────────────────────────────────────────────────────────

export const ATTENDANCE_DAY_STATUSES = [
  'open',
  'present',
  'closed',
  'requires_correction',
  'absent',
  'on_leave',
  'holiday',
  'weekend',
] as const;
export type AttendanceDayStatus = (typeof ATTENDANCE_DAY_STATUSES)[number];

export const ATTENDANCE_EVENT_TYPES = [
  'punch_in',
  'punch_out',
  'break_start',
  'break_end',
] as const;
export type AttendanceEventType = (typeof ATTENDANCE_EVENT_TYPES)[number];

export const CLIENT_SOURCES = ['web', 'pwa', 'desktop'] as const;
export type ClientSource = (typeof CLIENT_SOURCES)[number];

// ── Leave ───────────────────────────────────────────────────────────────────────

export const LEAVE_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'cancelled',
  'withdrawn',
] as const;
export type LeaveStatus = (typeof LEAVE_STATUSES)[number];

export const LEAVE_DAY_PARTS = ['full', 'half_am', 'half_pm'] as const;
export type LeaveDayPart = (typeof LEAVE_DAY_PARTS)[number];

export const LEAVE_ACCRUAL_MODES = ['annual_grant', 'monthly_accrual'] as const;
export type LeaveAccrualMode = (typeof LEAVE_ACCRUAL_MODES)[number];

export const LEAVE_LEDGER_REASONS = [
  'accrual',
  'application',
  'approval',
  'cancellation',
  'adjustment',
  'carry_forward',
] as const;
export type LeaveLedgerReason = (typeof LEAVE_LEDGER_REASONS)[number];

// ── Approvals ───────────────────────────────────────────────────────────────────

export const APPROVAL_TYPES = ['leave', 'attendance_correction', 'work_report'] as const;
export type ApprovalType = (typeof APPROVAL_TYPES)[number];

export const APPROVAL_REQUEST_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'cancelled',
  'escalated',
] as const;
export type ApprovalRequestStatus = (typeof APPROVAL_REQUEST_STATUSES)[number];

export const APPROVAL_STEP_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'skipped',
  'reassigned',
] as const;
export type ApprovalStepStatus = (typeof APPROVAL_STEP_STATUSES)[number];

export const APPROVER_RESOLVERS = [
  'reportingManager',
  'departmentHead',
  'role',
  'namedEmployee',
  'skipLevelManager',
] as const;
export type ApproverResolver = (typeof APPROVER_RESOLVERS)[number];

// ── Tasks ───────────────────────────────────────────────────────────────────────

export const TASK_STATUSES = ['todo', 'in_progress', 'blocked', 'in_review', 'done'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

/** Valid task transitions. Anything absent here is rejected with INVALID_TRANSITION. */
export const TASK_TRANSITIONS: Record<TaskStatus, readonly TaskStatus[]> = {
  todo: ['in_progress', 'blocked'],
  in_progress: ['blocked', 'in_review', 'done', 'todo'],
  blocked: ['todo', 'in_progress'],
  in_review: ['in_progress', 'done'],
  done: ['in_progress'],
};

export const TASK_PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

// ── Work reports ────────────────────────────────────────────────────────────────

export const WORK_REPORT_STATUSES = [
  'draft',
  'submitted',
  'under_review',
  'changes_requested',
  'reviewed',
] as const;
export type WorkReportStatus = (typeof WORK_REPORT_STATUSES)[number];

// ── Work sessions ───────────────────────────────────────────────────────────────

export const WORK_SESSION_STATUSES = ['active', 'paused', 'stopped', 'stale'] as const;
export type WorkSessionStatus = (typeof WORK_SESSION_STATUSES)[number];

/**
 * Valid work-session transitions. `stale` is server-assigned only — a client can never
 * request it. See docs/DESKTOP_APPLICATION_PLAN.md §5.
 */
export const WORK_SESSION_TRANSITIONS: Record<WorkSessionStatus, readonly WorkSessionStatus[]> = {
  stopped: ['active'],
  active: ['paused', 'stopped'],
  paused: ['active', 'stopped'],
  stale: [],
};

export const WORK_SESSION_EVENT_TYPES = [
  'start',
  'pause',
  'resume',
  'stop',
  'heartbeat',
  'idle_start',
  'idle_end',
  'disconnect',
  'reconnect',
  'stale_truncate',
] as const;
export type WorkSessionEventType = (typeof WORK_SESSION_EVENT_TYPES)[number];

// ── Communication ───────────────────────────────────────────────────────────────

export const ANNOUNCEMENT_STATUSES = [
  'draft',
  'scheduled',
  'published',
  'expired',
  'archived',
] as const;
export type AnnouncementStatus = (typeof ANNOUNCEMENT_STATUSES)[number];

export const ANNOUNCEMENT_AUDIENCE_TYPES = ['organization', 'department', 'role'] as const;
export type AnnouncementAudienceType = (typeof ANNOUNCEMENT_AUDIENCE_TYPES)[number];

/** Feed reactions — one per employee per post, like a social timeline. */
export const ANNOUNCEMENT_REACTIONS = ['like', 'love', 'celebrate', 'support', 'insight'] as const;

// ── Support tickets ─────────────────────────────────────────────────────────────

export const SUPPORT_TICKET_TYPES = ['technical', 'hr'] as const;
export type SupportTicketType = (typeof SUPPORT_TICKET_TYPES)[number];

export const SUPPORT_TICKET_STATUSES = ['open', 'in_progress', 'resolved', 'closed'] as const;
export type SupportTicketStatus = (typeof SUPPORT_TICKET_STATUSES)[number];

export const SUPPORT_EMPLOYEE_RESPONSES = [
  'confirm_resolved',
  'still_an_issue',
  'feedback',
] as const;
export type SupportEmployeeResponse = (typeof SUPPORT_EMPLOYEE_RESPONSES)[number];
export type AnnouncementReaction = (typeof ANNOUNCEMENT_REACTIONS)[number];

export const NOTIFICATION_CHANNELS = ['in_app', 'desktop', 'email', 'web_push'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const DELIVERY_STATUSES = ['queued', 'sent', 'skipped', 'failed'] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

// ── Documents ───────────────────────────────────────────────────────────────────

export const DOCUMENT_STATUSES = [
  'pending',
  'collected',
  'uploaded',
  'verified',
  'rejected',
] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

// ── Holidays ────────────────────────────────────────────────────────────────────

export const HOLIDAY_SCOPES = ['organization', 'location'] as const;
export type HolidayScope = (typeof HOLIDAY_SCOPES)[number];
